import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  CATEGORY_INFO,
  DISPOSAL_INFO,
  CARBON_FACTOR_INFO,
  DEFAULT_CARBON_FACTORS,
  DEFRA_FACTORS,
  parseCarbonFactors,
  type AppSetting,
  type AppSettingsFeatures,
  type SettingGroup,
  type CarbonFactorKey,
  type CarbonFactors,
  type CarbonRouting,
  type MaterialCategory,
  type DisposalMethod,
} from '@igf-minar/shared'
import { api, ApiError } from '../lib/api'
import { useCurrentUser, isSuperAdminRole } from '../lib/auth'

export const Route = createFileRoute('/admin/settings')({
  component: AdminSettingsPage,
})

const GROUP_LABEL: Record<SettingGroup, string> = {
  general: 'Almennt',
  svr2: 'SVR2 — gagnaþjónusta ÍGF',
  email: 'Tölvupóstur',
  auth: 'Auðkenning',
  ai: 'Gervigreind',
  carbon: 'Kolefnisspor',
}

const GROUP_ORDER: SettingGroup[] = ['general', 'svr2', 'email', 'auth', 'ai', 'carbon']

const FEATURE_LABEL: Record<string, string> = {
  svr2: 'Gagnasókn frá SVR2',
  email: 'Sending athugasemda í tölvupósti',
  openAi: 'Innsæi (OpenAI)',
}

function AdminSettingsPage() {
  const { data: me, isLoading: meLoading } = useCurrentUser()
  const qc = useQueryClient()
  const isSuper = isSuperAdminRole(me)

  const { data, isLoading, error } = useQuery({
    queryKey: ['admin', 'settings'],
    queryFn: () => api.admin.settings.get(),
    enabled: isSuper,
  })

  const [edits, setEdits] = useState<Record<string, string>>({})
  const [fieldErrors, setFieldErrors] = useState<Record<string, string[]>>({})
  const [saved, setSaved] = useState(false)
  const [confirming, setConfirming] = useState(false)

  const save = useMutation({
    mutationFn: (payload: Record<string, string>) => api.admin.settings.update(payload),
    onSuccess: (_res, payload) => {
      setConfirming(false)
      setEdits((prev) => {
        const next = { ...prev }
        for (const [k, v] of Object.entries(payload)) if (next[k] === v) delete next[k]
        return next
      })
      setFieldErrors({})
      setSaved(true)
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] })
    },
    onError: (err: unknown) => {
      setSaved(false)
      setConfirming(false)
      setFieldErrors(err instanceof ApiError ? err.issues?.fieldErrors ?? {} : {})
    },
  })

  const grouped = useMemo(() => {
    const byGroup = new Map<SettingGroup, AppSetting[]>()
    for (const setting of data?.settings ?? []) {
      if (setting.key === 'carbonFactors') continue
      const list = byGroup.get(setting.group) ?? []
      list.push(setting)
      byGroup.set(setting.group, list)
    }
    return GROUP_ORDER.filter((g) => byGroup.has(g)).map((g) => ({
      group: g,
      settings: byGroup.get(g)!,
    }))
  }, [data])

  const setValue = (key: string, value: string) => {
    setSaved(false)
    setConfirming(false)
    setEdits((prev) => ({ ...prev, [key]: value }))
  }

  const resetValue = (key: string) => {
    setConfirming(false)
    setEdits((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })
  }

  if (meLoading) return <PageShell><p className="text-gray-500">Hleð…</p></PageShell>

  if (!isSuper) {
    return (
      <PageShell>
        <div className="rounded-xl bg-amber-50 border border-amber-200 p-6 text-amber-900">
          <h2 className="font-semibold">Enginn aðgangur</h2>
          <p className="mt-1 text-sm">
            Aðeins superadmin má sjá og breyta stillingum kerfisins — þær geyma
            aðgangsorð að öðrum kerfum.
          </p>
        </div>
      </PageShell>
    )
  }

  const changes = (data?.settings ?? []).flatMap((setting) => {
    const value = edits[setting.key]
    if (value === undefined) return []
    if (setting.kind === 'secret' ? value === '' && !setting.hasValue : value === (setting.value ?? '')) return []
    return [{ setting, value }]
  })
  const dirty = changes.length > 0

  return (
    <PageShell>
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900">Stillingar</h1>
        <p className="text-sm text-gray-500 mt-1">
          Aðgangur að ytri kerfum og hegðun forritsins. Breytingar taka gildi strax —
          hvorki þarf að endurræsa né setja nýja útgáfu í loftið.
        </p>
      </div>

      {isLoading && <p className="text-gray-500">Sæki stillingar…</p>}
      {error && !save.isError && <p className="text-red-600">{(error as Error).message}</p>}

      {data && (
        <>
          <FeatureStatus features={data.features} />

          <div className="space-y-6">
            {grouped.map(({ group, settings }) => (
              <section key={group} className="bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
                <h2 className="px-5 py-3 bg-gray-50 border-b border-gray-100 text-sm font-semibold text-gray-700">
                  {GROUP_LABEL[group]}
                </h2>
                <div className="p-5 space-y-5">
                  {settings.map((setting) => (
                    <Field
                      key={setting.key}
                      setting={setting}
                      edit={edits[setting.key]}
                      errors={fieldErrors[setting.key]}
                      onChange={(value) => setValue(setting.key, value)}
                      onReset={() => resetValue(setting.key)}
                    />
                  ))}
                </div>
              </section>
            ))}
          </div>

          {confirming && dirty && (
            <div
              role="alertdialog"
              aria-labelledby="settings-confirm-title"
              className="mt-6 rounded-xl border border-amber-200 bg-amber-50 p-5 text-amber-900"
            >
              <h3 id="settings-confirm-title" className="font-semibold">Staðfestu breytingar</h3>
              <p className="mt-1 text-sm">Eftirfarandi stillingum verður breytt:</p>
              <ul className="mt-2 space-y-1 text-sm">
                {changes.map(({ setting, value }) => (
                  <li key={setting.key}>
                    <span className="font-medium">{setting.label}</span>{' '}
                    <span className="text-amber-800">
                      {setting.kind === 'secret'
                        ? value === ''
                          ? '— leyndarmál verður hreinsað'
                          : '— nýtt leyndarmál'
                        : value === ''
                          ? '— verður tómt'
                          : `— ${value}`}
                    </span>
                  </li>
                ))}
              </ul>
              <div className="mt-4 flex items-center gap-4">
                <button
                  onClick={() => save.mutate(Object.fromEntries(changes.map(({ setting, value }) => [setting.key, value])))}
                  disabled={save.isPending}
                  className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg px-4 py-2"
                >
                  {save.isPending ? 'Vista…' : 'Já, vista'}
                </button>
                <button
                  onClick={() => setConfirming(false)}
                  disabled={save.isPending}
                  className="text-sm text-amber-900 hover:underline"
                >
                  Til baka
                </button>
              </div>
            </div>
          )}

          <div className="mt-6 flex items-center gap-4">
            <button
              onClick={() => { setSaved(false); setConfirming(true) }}
              disabled={!dirty || save.isPending || confirming}
              className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg px-4 py-2"
            >
              Vista breytingar
            </button>
            {dirty && !save.isPending && (
              <button
                onClick={() => { setEdits({}); setFieldErrors({}); setConfirming(false) }}
                className="text-sm text-gray-500 hover:underline"
              >
                Hætta við
              </button>
            )}
            {saved && <span className="text-sm text-emerald-700">Stillingar vistaðar.</span>}
            {save.isError && Object.keys(fieldErrors).length === 0 && (
              <span className="text-sm text-red-600">{(save.error as Error).message}</span>
            )}
            {save.isError && Object.keys(fieldErrors).length > 0 && (
              <span className="text-sm text-red-600">Sumar stillingar eru ógildar — sjá að ofan.</span>
            )}
          </div>

          <CarbonFactorsSettings
            current={data.settings.find((st) => st.key === 'carbonFactors')?.value ?? null}
          />

          <CarbonRoutingSettings />

          <Svr2Diagnostics />
        </>
      )}
    </PageShell>
  )
}

const FACTOR_YEARS = [2020, 2021, 2022, 2023, 2024, 2025, 2026]
const FACTOR_KEYS = Object.keys(CARBON_FACTOR_INFO) as CarbonFactorKey[]

type FactorEdits = Record<string, { years: Record<number, string>; source: string }>

function factorsToEdits(factors: CarbonFactors | null): FactorEdits {
  const out: FactorEdits = {}
  for (const key of FACTOR_KEYS) {
    const active = factors?.[key] ?? DEFAULT_CARBON_FACTORS[key]
    const years: Record<number, string> = {}
    for (const y of FACTOR_YEARS) {
      const v = active.byYear[y]
      years[y] = typeof v === 'number' ? String(v) : ''
    }
    out[key] = { years, source: active.source ?? '' }
  }
  return out
}

function inheritedFactor(row: { years: Record<number, string> } | undefined, year: number): string | null {
  if (!row) return null
  const defined = FACTOR_YEARS.filter((y) => (row.years[y] ?? '').trim() !== '')
  if (defined.length === 0) return null
  let chosen = defined[0]
  for (const y of defined) { if (y <= year) chosen = y; else break }
  return row.years[chosen]
}

function editsToFactors(edits: FactorEdits): CarbonFactors {
  const out: Record<string, { byYear: Record<number, number>; source?: string }> = {}
  for (const key of FACTOR_KEYS) {
    const row = edits[key]
    if (!row) continue
    const byYear: Record<number, number> = {}
    for (const y of FACTOR_YEARS) {
      const raw = row.years[y]
      const n = raw != null && raw.trim() !== '' ? Number(raw.replace(',', '.')) : NaN
      if (Number.isFinite(n) && n >= 0) byYear[y] = n
    }
    if (Object.keys(byYear).length === 0) continue
    out[key] = { byYear, ...(row.source.trim() ? { source: row.source.trim() } : {}) }
  }
  return out as CarbonFactors
}

function CarbonFactorsSettings({ current }: { current: string | null }) {
  const qc = useQueryClient()
  const stored = useMemo(() => {
    if (!current) return null
    try {
      return parseCarbonFactors(JSON.parse(current))
    } catch {
      return null
    }
  }, [current])

  const [edits, setEdits] = useState<FactorEdits>(() => factorsToEdits(stored))
  const [saved, setSaved] = useState(false)
  // A ref, not state: the grid must re-sync when the server value changes, but
  // marking it dirty must not itself re-run the effect and undo the typing.
  const dirty = useRef(false)

  useEffect(() => {
    if (dirty.current) return
    setEdits(factorsToEdits(stored))
  }, [stored])

  const save = useMutation({
    mutationFn: (value: string) => api.admin.settings.update({ carbonFactors: value }),
    onSuccess: () => {
      dirty.current = false
      setSaved(true)
      qc.invalidateQueries({ queryKey: ['admin', 'settings'] })
      qc.invalidateQueries({ queryKey: ['companyData'] })
    },
  })

  const setCell = (key: string, year: number, value: string) => {
    setSaved(false)
    dirty.current = true
    setEdits((prev) => ({
      ...prev,
      [key]: { ...prev[key], years: { ...prev[key].years, [year]: value } },
    }))
  }
  const setSource = (key: string, value: string) => {
    setSaved(false)
    dirty.current = true
    setEdits((prev) => ({ ...prev, [key]: { ...prev[key], source: value } }))
  }

  return (
    <section className="mt-8 bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
      <h2 className="px-5 py-3 bg-gray-50 border-b border-gray-100 text-sm font-semibold text-gray-700">
        Kolefnisspor — losunarstuðlar
      </h2>
      <div className="p-5 space-y-4">
        <p className="text-xs text-gray-500 leading-relaxed">
          Stuðlar í kg CO₂íg á hvert kg úrgangs, eftir gagnaári. Ár án gildis notar næsta ár á undan.
          Skráðu heimild fyrir hverjum stuðli — hún birtist í aðferðafræðiskjölum. Sjálfgefin gildi eru
          UOS-blandan (íslenskir stuðlar Umhverfis- og orkustofnunar þar sem þeir eru til, DEFRA í eyður) —
          það er stefnan sem ÍGF samþykkti og kerfið notar sjálfkrafa þegar ekkert er vistað hér. Tóm tafla
          þýðir því UOS. „Setja gömlu DEFRA-stuðlana“ fyllir inn stuðlana sem Laufið notaði áður, til
          samanburðar — ekkert breytist fyrr en þú vistar, og vistun tekur gildi strax fyrir alla viðskiptavini.
        </p>

        <div className="overflow-x-auto">
          <table className="min-w-full text-sm">
            <thead>
              <tr className="text-left text-xs text-gray-500">
                <th className="py-2 pr-4 font-medium">Stuðull</th>
                {FACTOR_YEARS.map((y) => (
                  <th key={y} className="py-2 px-1 font-medium text-center">{y}</th>
                ))}
                <th className="py-2 pl-3 font-medium">Heimild</th>
              </tr>
            </thead>
            <tbody>
              {FACTOR_KEYS.map((key) => (
                <tr key={key} className="border-t border-gray-100 align-top">
                  <td className="py-2 pr-4">
                    <div className="font-medium text-gray-800">{CARBON_FACTOR_INFO[key].label}</div>
                    <div className="text-xs text-gray-400">{CARBON_FACTOR_INFO[key].appliesTo}</div>
                  </td>
                  {FACTOR_YEARS.map((y) => {
                    const own = edits[key]?.years[y] ?? ''
                    const inherited = own.trim() === '' ? inheritedFactor(edits[key], y) : null
                    return (
                      <td key={y} className="py-2 px-1">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={own}
                          onChange={(e) => setCell(key, y, e.target.value)}
                          placeholder={inherited ?? ''}
                          title={inherited ? `Erfir ${inherited} — skrifaðu gildi til að yfirskrifa fyrir ${y}` : undefined}
                          className="w-24 rounded-lg border border-gray-200 px-2 py-1.5 text-xs text-right placeholder:text-gray-300 placeholder:italic focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </td>
                    )
                  })}
                  <td className="py-2 pl-3">
                    <input
                      type="text"
                      value={edits[key]?.source ?? ''}
                      onChange={(e) => setSource(key, e.target.value)}
                      placeholder="t.d. UOS 7. útgáfa 2025"
                      className="w-56 rounded-lg border border-gray-200 px-2 py-1.5 text-xs focus:outline-none focus:ring-2 focus:ring-emerald-500"
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => save.mutate(JSON.stringify(editsToFactors(edits)))}
            disabled={save.isPending}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg px-4 py-2"
          >
            {save.isPending ? 'Vista…' : 'Vista stuðla'}
          </button>
          <button
            onClick={() => { setSaved(false); dirty.current = true; setEdits(factorsToEdits(DEFRA_FACTORS)) }}
            disabled={save.isPending}
            className="rounded-lg border border-gray-300 text-gray-600 hover:bg-gray-50 text-sm font-medium px-4 py-2"
          >
            Setja gömlu DEFRA-stuðlana (samanburður)
          </button>
          <button
            onClick={() => save.mutate('')}
            disabled={save.isPending}
            className="text-sm text-gray-500 hover:underline"
          >
            Endurstilla í sjálfgefið (UOS)
          </button>
          {saved && <span className="text-sm text-emerald-700">Stuðlar vistaðir.</span>}
          {save.isError && <span className="text-sm text-red-600">{(save.error as Error).message}</span>}
        </div>
      </div>
    </section>
  )
}

// The disposal routes a category's waste can be split across, in display order.
// "Brennsla" is energy recovery (orkunýting) in ÍGF's terminology.
const ROUTING_DISPOSALS: DisposalMethod[] = ['endurvinnsla', 'orkunyting', 'brennsla', 'urdun']
const DISPOSAL_EXTRA_LABEL: Partial<Record<DisposalMethod, string>> = {
  brennsla: ' (orkunýting)',
}

type RoutingEdits = Record<string, Record<string, string>>

function routingToEdits(routing: CarbonRouting | null | undefined): RoutingEdits {
  const out: RoutingEdits = {}
  if (!routing) return out
  for (const [cat, split] of Object.entries(routing)) {
    if (!split) continue
    const row: Record<string, string> = {}
    for (const d of ROUTING_DISPOSALS) {
      const v = split[d]
      if (typeof v === 'number' && v > 0) row[d] = String(v)
    }
    if (Object.keys(row).length > 0) out[cat] = row
  }
  return out
}

function editsToRouting(edits: RoutingEdits): CarbonRouting {
  const routing: Record<string, Record<string, number>> = {}
  for (const [cat, row] of Object.entries(edits)) {
    const split: Record<string, number> = {}
    for (const d of ROUTING_DISPOSALS) {
      const raw = row[d]
      const n = raw != null && raw.trim() !== '' ? Number(raw.replace(',', '.')) : NaN
      if (Number.isFinite(n) && n > 0) split[d] = n
    }
    if (Object.keys(split).length > 0) routing[cat] = split
  }
  return routing as CarbonRouting
}

/**
 * Per-company override of how each waste category splits across disposal routes.
 * The footprint is very sensitive to this (landfill ≈ 23× recycling/energy
 * recovery), so letting a customer enter their real split — e.g. unsorted waste
 * mostly to energy recovery, not landfill — is what makes the numbers accurate.
 */
function CarbonRoutingSettings() {
  const qc = useQueryClient()
  const companiesQuery = useQuery({
    queryKey: ['companies'],
    queryFn: () => api.companies(),
  })
  const companies = useMemo(
    () => (companiesQuery.data?.companies ?? []).slice().sort((a, b) => a.name.localeCompare(b.name, 'is')),
    [companiesQuery.data]
  )

  const [companyId, setCompanyId] = useState('')
  const effectiveCompanyId = companyId || companies[0]?.id || ''
  const selected = companies.find((c) => c.id === effectiveCompanyId)

  const [edits, setEdits] = useState<RoutingEdits>({})
  const [saved, setSaved] = useState(false)
  const dirty = useRef(false)
  const storedRouting = selected?.carbonRouting

  // Switching company always discards the grid; a background refetch of the
  // same company must not, or alt-tabbing mid-edit wipes what was typed.
  useEffect(() => {
    dirty.current = false
    setSaved(false)
    setEdits(routingToEdits(storedRouting))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [effectiveCompanyId])

  useEffect(() => {
    if (dirty.current) return
    setEdits(routingToEdits(storedRouting))
  }, [storedRouting])

  const save = useMutation({
    mutationFn: () => api.admin.companies.setCarbonRouting(effectiveCompanyId, editsToRouting(edits)),
    onSuccess: () => {
      dirty.current = false
      setSaved(true)
      qc.invalidateQueries({ queryKey: ['companies'] })
      qc.invalidateQueries({ queryKey: ['companyData'] })
      qc.invalidateQueries({ queryKey: ['company', effectiveCompanyId] })
    },
  })

  const categories = Object.keys(CATEGORY_INFO) as MaterialCategory[]

  const setCell = (cat: string, disposal: string, value: string) => {
    setSaved(false)
    dirty.current = true
    setEdits((prev) => {
      const row = { ...(prev[cat] ?? {}) }
      if (value.trim() === '') delete row[disposal]
      else row[disposal] = value
      const next = { ...prev }
      if (Object.keys(row).length === 0) delete next[cat]
      else next[cat] = row
      return next
    })
  }

  const rowSum = (cat: string): number =>
    ROUTING_DISPOSALS.reduce((s, d) => {
      const raw = edits[cat]?.[d]
      const n = raw != null && raw.trim() !== '' ? Number(raw.replace(',', '.')) : 0
      return s + (Number.isFinite(n) ? n : 0)
    }, 0)

  return (
    <section className="mt-8 bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
      <h2 className="px-5 py-3 bg-gray-50 border-b border-gray-100 text-sm font-semibold text-gray-700">
        Kolefnisspor — ráðstöfunarhlutföll per fyrirtæki
      </h2>
      <div className="p-5 space-y-4">
        <p className="text-xs text-gray-500 leading-relaxed">
          Sláðu inn hvernig úrgangur hvers flokks skiptist milli farvega hjá þessu fyrirtæki (t.d. óflokkað:
          60 í orkunýtingu, 30 í endurvinnslu, 10 í urðun). Gildin eru hlutfallsleg — þau þurfa ekki að leggja
          nákvæmlega í 100. Tómur flokkur notar sjálfgefna meðhöndlun. Urðun blandaðs úrgangs er ~23× hærri en
          endurvinnsla/orkunýting, svo þetta hefur mikil áhrif á sporið.
        </p>

        <label className="block max-w-sm">
          <span className="block text-xs font-medium text-gray-600 mb-1">Fyrirtæki</span>
          <select
            value={effectiveCompanyId}
            onChange={(e) => setCompanyId(e.target.value)}
            className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
          >
            {companies.map((c) => (
              <option key={c.id} value={c.id}>{c.name}</option>
            ))}
          </select>
        </label>

        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wide text-gray-400 border-b border-gray-100">
                <th className="py-2 pr-3 font-medium">Efnisflokkur</th>
                {ROUTING_DISPOSALS.map((d) => (
                  <th key={d} className="py-2 px-2 font-medium text-right">
                    {DISPOSAL_INFO[d]?.name ?? d}{DISPOSAL_EXTRA_LABEL[d] ?? ''}
                  </th>
                ))}
                <th className="py-2 pl-2 font-medium text-right">Summa</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((cat) => {
                const sum = rowSum(cat)
                const active = !!edits[cat]
                return (
                  <tr key={cat} className="border-b border-gray-50 last:border-0">
                    <td className="py-1.5 pr-3">
                      <span className="flex items-center gap-2 text-gray-700">
                        <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: CATEGORY_INFO[cat]?.color ?? '#94A3B8' }} />
                        {CATEGORY_INFO[cat]?.name ?? cat}
                      </span>
                    </td>
                    {ROUTING_DISPOSALS.map((d) => (
                      <td key={d} className="py-1.5 px-2 text-right">
                        <input
                          type="text"
                          inputMode="decimal"
                          value={edits[cat]?.[d] ?? ''}
                          onChange={(e) => setCell(cat, d, e.target.value)}
                          placeholder="—"
                          className="w-16 rounded-md border border-gray-200 px-2 py-1 text-right text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
                        />
                      </td>
                    ))}
                    <td className={`py-1.5 pl-2 text-right tabular-nums ${active ? 'text-gray-600' : 'text-gray-300'}`}>
                      {active ? sum : '—'}
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>

        <div className="flex items-center gap-4">
          <button
            onClick={() => save.mutate()}
            disabled={!effectiveCompanyId || save.isPending}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg px-4 py-2"
          >
            {save.isPending ? 'Vista…' : 'Vista hlutföll'}
          </button>
          {saved && !save.isPending && <span className="text-sm text-emerald-700">Hlutföll vistuð.</span>}
          {save.isError && <span className="text-sm text-red-600">{(save.error as Error).message}</span>}
        </div>
      </div>
    </section>
  )
}

const MONTHS_IS = ['jan', 'feb', 'mar', 'apr', 'maí', 'jún', 'júl', 'ágú', 'sep', 'okt', 'nóv', 'des']

/** Live SVR2 fetch (bypassing cache) + cache purge, so a silent "200 OK, empty"
 *  becomes visible and a poisoned settled-month cache can be re-fetched. */
function Svr2Diagnostics() {
  const now = new Date()
  const companiesQuery = useQuery({
    queryKey: ['admin', 'companies'],
    queryFn: () => api.admin.companies.list(),
  })
  const companies = useMemo(
    () => (companiesQuery.data?.companies ?? []).filter((c) => c.kennitala),
    [companiesQuery.data]
  )

  const [companyId, setCompanyId] = useState('')
  const [year, setYear] = useState(now.getFullYear())
  const [month, setMonth] = useState(now.getMonth() + 1)

  const effectiveCompanyId = companyId || companies[0]?.id || ''

  const test = useMutation({
    mutationFn: () => api.admin.svr2.test({ companyId: effectiveCompanyId, year, month }),
  })
  const clear = useMutation({
    mutationFn: () => api.admin.svr2.clearCache({ companyId: effectiveCompanyId, year }),
  })

  const years = Array.from({ length: now.getFullYear() - 2014 }, (_, i) => now.getFullYear() - i)

  const STATE_STYLE: Record<string, string> = {
    ok: 'bg-emerald-50 border-emerald-200 text-emerald-900',
    empty: 'bg-amber-50 border-amber-200 text-amber-900',
    not_configured: 'bg-red-50 border-red-200 text-red-900',
    fetch_failed: 'bg-red-50 border-red-200 text-red-900',
  }

  return (
    <section className="mt-8 bg-white shadow-sm rounded-xl border border-gray-100 overflow-hidden">
      <h2 className="px-5 py-3 bg-gray-50 border-b border-gray-100 text-sm font-semibold text-gray-700">
        SVR2 — greining og cache
      </h2>
      <div className="p-5 space-y-4">
        <p className="text-xs text-gray-500">
          „Prófa tengingu" gerir <strong>lifandi</strong> sókn framhjá cache og sýnir HTTP-svar + fjölda
          færslna — svo þú sjáir strax hvort aðgangurinn nær í gögn. „Hreinsa cache" eyðir vistuðum
          færslum svo appið sæki aftur (uppgerð ár eru annars cache-uð að eilífu).
        </p>

        <div className="grid gap-3 sm:grid-cols-3">
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Fyrirtæki</span>
            <select
              value={effectiveCompanyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {companies.map((c) => (
                <option key={c.id} value={c.id}>{c.name} ({c.kennitala})</option>
              ))}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Ár</span>
            <select
              value={year}
              onChange={(e) => setYear(Number(e.target.value))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {years.map((y) => <option key={y} value={y}>{y}</option>)}
            </select>
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-gray-600 mb-1">Mánuður (fyrir prófun)</span>
            <select
              value={month}
              onChange={(e) => setMonth(Number(e.target.value))}
              className="w-full rounded-lg border border-gray-200 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500"
            >
              {MONTHS_IS.map((m, i) => <option key={m} value={i + 1}>{i + 1}. {m}</option>)}
            </select>
          </label>
        </div>

        <div className="flex flex-wrap items-center gap-3">
          <button
            onClick={() => test.mutate()}
            disabled={!effectiveCompanyId || test.isPending}
            className="bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white text-sm font-medium rounded-lg px-4 py-2"
          >
            {test.isPending ? 'Prófa…' : 'Prófa tengingu'}
          </button>
          <button
            onClick={() => { if (confirm(`Hreinsa SVR2-cache fyrir valið fyrirtæki árið ${year}?`)) clear.mutate() }}
            disabled={!effectiveCompanyId || clear.isPending}
            className="border border-gray-200 hover:bg-gray-50 disabled:opacity-40 text-gray-700 text-sm font-medium rounded-lg px-4 py-2"
          >
            {clear.isPending ? 'Hreinsa…' : `Hreinsa cache (${year})`}
          </button>
        </div>

        {test.isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {(test.error as Error).message}
          </div>
        )}
        {test.data && (
          <div className={`rounded-lg border p-3 text-sm ${STATE_STYLE[test.data.state] ?? 'bg-gray-50 border-gray-200'}`}>
            <p className="font-semibold">
              {test.data.state === 'ok' && '✓ Tengingin virkar'}
              {test.data.state === 'empty' && '⚠ HTTP 200 — engar færslur'}
              {test.data.state === 'not_configured' && '✕ SVR2 ekki stillt'}
              {test.data.state === 'fetch_failed' && '✕ Sókn mistókst'}
            </p>
            <p className="mt-1">{test.data.message}</p>
            {test.data.elapsedMs != null && (
              <p className="mt-1 text-xs opacity-70">CustId {test.data.custId} · {test.data.elapsedMs} ms</p>
            )}
          </div>
        )}
        {clear.isError && (
          <div className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-800">
            {(clear.error as Error).message}
          </div>
        )}
        {clear.data && (
          <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-900">
            {clear.data.message}
          </div>
        )}
      </div>
    </section>
  )
}

function FeatureStatus({ features }: { features: AppSettingsFeatures }) {
  const missing = Object.entries(features).filter(([, ok]) => !ok)
  if (missing.length === 0) {
    return (
      <div className="mb-6 rounded-xl bg-emerald-50 border border-emerald-200 p-4 text-sm text-emerald-900">
        Allar einingar eru stilltar.
      </div>
    )
  }

  return (
    <div className="mb-6 rounded-xl bg-amber-50 border border-amber-200 p-4 text-sm text-amber-900">
      <p className="font-semibold">Eftirfarandi einingar eru ekki stilltar og virka því ekki:</p>
      <ul className="mt-2 list-disc list-inside space-y-0.5">
        {missing.map(([key]) => <li key={key}>{FEATURE_LABEL[key] ?? key}</li>)}
      </ul>
    </div>
  )
}

function Field({
  setting,
  edit,
  errors,
  onChange,
  onReset,
}: {
  setting: AppSetting
  edit: string | undefined
  errors: string[] | undefined
  onChange: (value: string) => void
  onReset: () => void
}) {
  const touched = edit !== undefined
  const [revealed, setRevealed] = useState(false)
  const [unlocked, setUnlocked] = useState(false)
  const isSecret = setting.kind === 'secret'
  const lockable = isSecret || setting.group === 'svr2'
  const locked = lockable && !unlocked && !touched
  const inputClass =
    'w-full rounded-lg border px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-emerald-500 ' +
    (errors ? 'border-red-300' : 'border-gray-200')

  return (
    <div>
      <label className="block text-sm font-medium text-gray-700 mb-1">
        {setting.label}
        {setting.kind === 'secret' && (
          <span className="ml-2 text-xs font-normal text-gray-400">leyndarmál</span>
        )}
      </label>

      {setting.kind === 'bool' ? (
        <label className="inline-flex items-center gap-2 text-sm text-gray-700">
          <input
            type="checkbox"
            checked={(touched ? edit : setting.value) === 'true'}
            onChange={(e) => onChange(e.target.checked ? 'true' : 'false')}
            className="rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
          />
          Virkt
        </label>
      ) : setting.kind === 'enum' ? (
        <select
          value={touched ? edit : setting.value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass}
        >
          {(setting.options ?? []).map((option) => (
            <option key={option} value={option}>{option}</option>
          ))}
        </select>
      ) : setting.kind === 'text' ? (
        <textarea
          rows={6}
          value={touched ? edit : setting.value ?? ''}
          onChange={(e) => onChange(e.target.value)}
          className={inputClass + ' resize-y'}
        />
      ) : locked ? (
        <div className="flex gap-2">
          <div className="w-full rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3 py-2 text-sm text-[var(--muted)]">
            {isSecret ? (setting.hasValue ? '•••••••• (stillt)' : 'Ekki stillt') : setting.value || 'Ekki stillt'}
          </div>
          <button
            type="button"
            onClick={() => setUnlocked(true)}
            aria-label={`Breyta ${setting.label}`}
            className="shrink-0 rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3 text-sm text-[var(--muted)] hover:text-[var(--fg)]"
          >
            Breyta
          </button>
        </div>
      ) : (
        <div className="flex gap-2">
          <input
            autoFocus={lockable}
            type={isSecret && !revealed ? 'password' : 'text'}
            inputMode={setting.kind === 'int' ? 'numeric' : undefined}
            value={touched ? edit : setting.value ?? ''}
            onChange={(e) => {
              if (document.activeElement !== e.currentTarget) return
              onChange(e.target.value)
            }}
            placeholder={
              isSecret
                ? setting.hasValue
                  ? '•••••••• — sláðu inn nýtt gildi til að skipta um'
                  : 'Ekki stillt'
                : undefined
            }
            name={`cfg-${fieldToken(setting.key)}`}
            autoComplete={isSecret ? 'new-password' : 'off'}
            spellCheck={false}
            data-1p-ignore="true"
            data-lpignore="true"
            data-bwignore="true"
            data-form-type="other"
            className={inputClass}
          />
          {isSecret && (
            <button
              type="button"
              onClick={() => setRevealed((r) => !r)}
              aria-label={`${revealed ? 'Fela' : 'Sýna'} ${setting.label}`}
              aria-pressed={revealed}
              className="shrink-0 rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3 text-sm text-[var(--muted)] hover:text-[var(--fg)]"
            >
              {revealed ? 'Fela' : 'Sýna'}
            </button>
          )}
          {lockable && (
            <button
              type="button"
              onClick={() => { setUnlocked(false); setRevealed(false); onReset() }}
              aria-label={`Hætta við breytingu á ${setting.label}`}
              className="shrink-0 rounded-lg border border-[var(--border)] bg-[var(--surface)] px-3 text-sm text-[var(--muted)] hover:text-[var(--fg)]"
            >
              Hætta við
            </button>
          )}
        </div>
      )}

      {setting.description && (
        <p className="mt-1 text-xs text-gray-500">{setting.description}</p>
      )}
      {errors?.map((message) => (
        <p key={message} className="mt-1 text-xs text-red-600">{message}</p>
      ))}
    </div>
  )
}

function fieldToken(key: string): string {
  let hash = 0
  for (const ch of key) hash = (hash * 31 + ch.charCodeAt(0)) >>> 0
  return hash.toString(36)
}

function PageShell({ children }: { children: React.ReactNode }) {
  return <div className="max-w-[1100px] mx-auto px-4 sm:px-6 lg:px-8 py-8">{children}</div>
}
